import { useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  TextArea,
  TextField,
  TextLabel,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import type { Tag } from '@customerio/hydra-ui-api';
import { GenerateDescriptionButton } from '../components/generate-description-button';
import * as generateStyles from '../components/generate-description-button.css';
import { TagEditor } from '../components/tag-editor';
import { descriptionCounter, descriptionError } from '../utils/editing';
import type { DataIndexAnalyticsType } from '../utils/types';

/**
 * The description and tags of one item, changed together.
 *
 * The name is shown but not editable: it is what ingestion writes, and this
 * page describes it rather than renames it.
 *
 * Not the ADR-0001 form pattern. Of the two values here, only the description
 * is a field, and its one rule is the length Pluma already counts under it;
 * the tags are a picker that creates records of its own while it is open,
 * which is why the save waits on `isNaming` rather than on a schema. A Zod
 * resolver would own neither value. FPS-4010 revisits this when the drawer
 * grows the same editor.
 */

export type EditDetailsModalData = {
  workspaceId: number;
  name: string;
  /** The description the item shows, which is what editing starts from. */
  description: string;
  tags: readonly Tag[];
  type: DataIndexAnalyticsType;
  /** Absent for a person attribute or an event, which belong to no object type. */
  objectTypeId?: number;
  /** Whether the viewer may change the tags, which needs its own permission. */
  canEditTags: boolean;
  /** Saves both halves; resolves to whether that worked. */
  onSave: (values: { description: string; tagIds: number[] }) => Promise<boolean>;
};

export function EditDetailsModal({ data, modalState, onClose }: PlumaManagedModalProps<EditDetailsModalData>) {
  const [description, setDescription] = useState(data.description);
  const [tagIds, setTagIds] = useState(() => data.tags.map((tag) => tag.id));
  const [isSaving, setIsSaving] = useState(false);
  // A tag being named is not a tag yet, and saving through it would drop the
  // one the reader is in the middle of writing.
  const [isNaming, setIsNaming] = useState(false);

  const error = descriptionError(description);
  // Nothing to save until something differs from what the item has, as
  // `details-edit-modal`'s `hasChanges` gates it: an edit form whose values
  // are unchanged has no action to take.
  const hasChanges = description !== data.description || !sameTagIds(tagIds, data.tags);

  const save = async () => {
    // The gate, not the button: Pluma's disabled Button does block the click,
    // but a save is a decision about values, and the rule this repo writes
    // down keeps that check in the handler. The drawer's save reads the same.
    if (isNaming || !hasChanges || error != null) {
      return;
    }
    setIsSaving(true);
    const saved = await data.onSave({ description, tagIds });
    setIsSaving(false);

    if (saved) {
      onClose();
    }
  };

  return (
    <Modal state={modalState} title="Edit attribute details" onClose={onClose}>
      <ModalBody>
        <Stack gap="250">
          <TextField label="Name" value={data.name} isDisabled />

          {/* The generate button sits inside the field's top-right corner,
              and the field does not resize, as `details-edit-modal` has it. */}
          <Box className={generateStyles.fieldWithTopEndButton}>
            <TextArea
              label="Description"
              placeholder="Enter a description…"
              value={description}
              error={error}
              description={descriptionCounter(description)}
              rows={4}
              resize="none"
              onInput={(event) => setDescription(event.target.value)}
            />
            <Box className={generateStyles.insideFieldTopEnd}>
              <GenerateDescriptionButton
                workspaceId={data.workspaceId}
                type={data.type}
                name={data.name}
                objectTypeId={data.objectTypeId}
                isDisabled={isSaving}
                onGenerated={setDescription}
              />
            </Box>
          </Box>

          <Stack gap="050">
            <TextLabel>Tags</TextLabel>
            <TagEditor
              workspaceId={data.workspaceId}
              tagIds={tagIds}
              tags={data.tags}
              canEdit={data.canEditTags}
              hasPermission={data.canEditTags}
              onSave={() => Promise.resolve(true)}
              bulk={{ tagIds, onChange: setTagIds, onNaming: setIsNaming }}
            />
          </Stack>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" isDisabled={isSaving} onClick={() => onClose()}>
          Cancel
        </Button>
        {/* Disabled while nothing has changed — an edit with no changes has
            nothing to submit — and while a tag is half-named, which is not
            about the values in the form. A description too long still
            reaches the handler, which refuses it under the field's error. */}
        <Button variant="primary" isDisabled={!hasChanges || isNaming} isLoading={isSaving} onClick={() => void save()}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/** Whether two sets of tag ids hold the same tags, in any order. */
function sameTagIds(tagIds: readonly number[], tags: readonly Tag[]): boolean {
  const original = new Set(tags.map((tag) => tag.id));
  return tagIds.length === original.size && tagIds.every((id) => original.has(id));
}
